<template>
    <div>
        <loading-indicator :clearBackground="true" :fullParent="false" :isLoading="isLoading" />
        <div v-if="paging && pointLogs.length > 0">
            <div class="text-right paging-information d-flex justify-content-end align-items-center">
                <span>
                    {{$t('paging_information_entry', this.paging)}}
                </span>
                <span v-if="this.paging.last_page > 0">
                    &nbsp;{{$t('paging_information_page', this.paging)}}
                </span>
            </div>
            <pagination :paging="paging" :is-loading="isLoading" @pageClick="(page) => {this.loadPage(page)}"/>
        </div>
        <table v-if="pointLogs.length" class="w-100 ding-table" border=0>
            <thead>
                <th>{{$t('date')}}</th>
                <th class="d-none d-sm-table-cell min-100">{{$t('reason')}}</th>
                <th class="d-none d-sm-table-cell">{{$t('points_changed')}}</th>
                <th class="d-sm-none min-100">{{$t('reason_and_points_changed')}}</th>
                <th>{{$t('current_points_left')}}</th>
            </thead>
            <tbody>
                <tr v-for="p in formattedPointLogs" :key="p.id" :class="{'dark-background': !customerId}" class="rounded-0">
                    <td>{{ formatDate(p.created_at) }}</td>
                    <td class="d-none d-sm-table-cell white-space-pre-line">{{ p.description || '' }}</td>
                    <td>
                        <div class="d-sm-none white-space-pre-line">{{ p.description || '' }}</div>
                        <div :class="{'text-danger': p.points < 0, 'text-success': p.points > 0}">
                            <span v-b-tooltip.click.hover.html.top="{ title: p.batch ? getBatchHintHTML(p.batch) : null }">
                                {{ (p.points > 0 ? ('+' + p.points) : p.points) + $t('points') }}
                            </span>
                        </div>
                        <div v-if="p.points > 0" class="text-muted">
                            <small v-if="p.expired_at">{{ $t('expired_at', {date: formatExpirationDate(p.expired_at)}) }}</small>
                            <small v-else>{{ $t('no_expiration_date') }}</small>
                        </div>
                    </td>
                    <td>{{ p.total_points_left + $t('points') }}</td>
                </tr>
            </tbody>
        </table>
        <div v-else class="text-center text-muted pt-4">
            <small>{{ $t('no_pointLogs') }}</small>
        </div>
    </div>
</template>

<script>
import api from '../../mixins/api';
import promptConfirm from '../../mixins/prompt-confirm';
import Pagination from '../common/Pagination.vue';
import loadingIndicator from '../../components/common/LoadingIndicator';
import moment from 'moment';
import 'moment-timezone';

export default {
    props: {
        customerId: { required: false}, // 如果是廠商後台看的話，會直接指名 customerId
        provider: { type: Object, required: true}
    },
    components: { loadingIndicator, Pagination },
    mixins: [api, promptConfirm],
    mounted() {
        this.loadPage(1);
    },
    data() {
        return {
            isLoading: false,
            pointLogs: [],
            paging: null
        }
    },
    computed: {
        formattedPointLogs() {
            const formattedPointLogsObject = {};
            this.pointLogs.forEach(item => {
                if (item.batch_id && !formattedPointLogsObject[item.batch_id]) {
                    formattedPointLogsObject[item.batch_id] = [];
                } else if (!item.batch_id && !formattedPointLogsObject[item.id]) {
                    formattedPointLogsObject[item.id] = [];
                }

                if (item.batch_id) {
                    formattedPointLogsObject[item.batch_id].push(item);
                } else {
                    formattedPointLogsObject[item.id].push(item);
                }
            })
            const formattedPointLogsArray = Object.values(formattedPointLogsObject);

            return formattedPointLogsArray.map(items => {
                if (items.length > 1) {
                    const totalPoints = items.reduce((total, item) => total + item.points, 0);
                    const totalPointsLeft = items.reduce((total, item) => {
                        if (item.points_left) {
                            return total + item.points_left;
                        } else {
                            return total;
                        }
                    }, 0);

                    return {
                        ...items[0],
                        points: totalPoints,
                        points_left: totalPointsLeft ?? null,
                        expired_at: null,
                        batch: items
                    }
                } else {
                    return items[0];
                }
            }).sort((a, b) => b.id - a.id);
        }
    },
    methods: {
        formatDate(date) {
            return moment.tz(date, 'GMT').tz(this.provider.timezone).format('YYYY-MM-DD A h:mm');
        },
        formatExpirationDate(date) {
            return moment.tz(date, 'GMT').tz(this.provider.timezone).format('YYYY-MM-DD');
        },
        loadPage(page) {
            this.isLoading = true;
            if (!this.customerId) {
                this.getCustomerPointLogs(page, (response, error) => {
                    this.isLoading = false;
                    if (error) {
                        this.promptAlert(error);
                    } else {
                        this.pointLogs = response.data.data;
                        this.paging = response.data;
                    }
                })
            } else {
                this.getCustomerPointLogsByAdmin(this.customerId, page, (response, error) => {
                    this.isLoading = false;
                    if (error) {
                        this.promptAlert(error);
                    } else {
                        this.pointLogs = response.data.data;
                        this.paging = response.data;
                    }
                })
            }
        },
        getBatchHintHTML(batch) {
            return batch.map(item => {
                if (item.points > 0) {
                    if (item.expired_at) {
                        return '+' + item.points + ' ' + this.$t('expired_at', {date: this.formatExpirationDate(item.expired_at)});
                    } else {
                        return '+' + item.points + ' ' + this.$t('no_expiration_date');
                    }
                } else {
                    return item.points.toString();
                }
            }).join('<br>');
        }
    }
}
</script>

<style scoped lang="scss">
    .ding-table {
        table-layout: auto;

        th {
            white-space: nowrap;
        }
    }

    .dark-background {
        background-color: #F6F6F6;
    }

    .paging-information {
        font-size: 12px;
        margin: 16px 0px;
        color: #90A4AE;
    }

    .min-100 {
        min-width: 100px;
    }

</style>

<i18n>
{
    "en": {
        "no_pointLogs": "No point records",
        "points": "points",
        "no_expiration_date": "(permanent)",
        "date": "Date",
        "expired_at": "(expired at {date})",
        "reason": "Reason",
        "points_changed": "Points Changes",
        "reason_and_points_changed": "Reason/Points Changes",
        "current_points_left": "Points Left",
        "paging_information_entry": "Total {total} records",
        "paging_information_page": "({current_page}/{last_page} pages)"
    },
    "zh": {
        "no_pointLogs": "目前沒有點數紀錄",
        "points": "點",
        "no_expiration_date": "(永久有效)",
        "date": "日期",
        "expired_at": "({date} 到期)",
        "reason": "原因",
        "points_changed": "點數變動",
        "reason_and_points_changed": "原因/點數變動",
        "current_points_left": "當下剩餘點數",
        "paging_information_entry": "共 {total} 筆",
        "paging_information_page": " ({current_page}/{last_page} 頁)"
    }
}
</i18n>